import { useEffect } from 'react';
import { useRouter } from 'next/router';

// Services
import { SchedulerNotificationService } from '@/components/composite/Notifications/Scheduler/services/SchedulerNotificationService';

// Hooks
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useSelectedSocialProfileById from '@/utils/hooks/socialprofiles/useSelectedSocialProfileById';
import useUser from '@/utils/hooks/swr/useUser';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Types
import type { SocialProfileType } from '@/types/socialProfileType';
import { type NotificationSchedulerProps, schedulerTypes } from '@/utils/hooks/context/toast/types';

// Constants
import {
    NOTIFICATION_CONFIGS,
    TIME_FORMAT_DEFAULT,
    TIMEZONE_DEFAULT
} from '@/components/composite/Notifications/Scheduler/const';

// Components
import Icon from '@/components/base/Icon';
import Notification from '@/components/base/Notification';
import PostThumbnail from '@/components/composite/Notifications/Collaboration/components/PostThumbnail';

// Routes
import { SCHEDULER } from '@/routes/privateRoutes';

type SchedulerNotificationProps = {
    notification: NotificationSchedulerProps['notification'];
};

export const SchedulerNotification = ({ notification }: SchedulerNotificationProps) => {
    const router = useRouter();
    const { user } = useUser();
    const { setActiveSocialProfile } = useActiveSocialProfile();
    const { selectedSocialProfile } = useSelectedSocialProfileById(
        notification.data?.social_profile_id as SocialProfileType['id']
    );
    const { formatDateTime } = useFormatDateTime(user?.settings?.timezone ?? TIMEZONE_DEFAULT);

    const userTimeFormat: '24' | '12' = user?.settings?.time_format ?? TIME_FORMAT_DEFAULT;

    const getNotificationUrl = () => {
        const postId = notification.data?.scheduler_post?.id;
        const socialProfileId = notification.data?.social_profile_id;

        if (!postId) {
            return SCHEDULER;
        }

        return SchedulerNotificationService.getNotificationUrl(notification.type, {
            postId,
            socialProfileId
        });
    };

    const url = getNotificationUrl();

    // Prefetch Overview
    useEffect(() => {
        router.prefetch(url);
    }, []);

    const getNotificationConfig = () => {
        const baseConfig = NOTIFICATION_CONFIGS[notification.type];
        if (!baseConfig) return null;

        return {
            ...baseConfig,
            getTitleValues: () => {
                switch (notification.type) {
                    case schedulerTypes.POST_UPDATED:
                    case schedulerTypes.POST_REJECTED_INFO:
                    case schedulerTypes.MULTIPOST_APPROVED_INFO:
                    case schedulerTypes.POST_APPROVED_INFO:
                    case schedulerTypes.POST_REJECTED_ACTION_REQUIRED:
                        return { name: notification?.data?.username };

                    case schedulerTypes.POST_PUBLISHED_FAILED:
                        return {
                            slug: selectedSocialProfile?.slug,
                            social_network: selectedSocialProfile?.social_network_type
                        };

                    default:
                        return null;
                }
            },
            getDescriptionValues: () => {
                switch (notification.type) {
                    case schedulerTypes.POST_UPDATED:
                    case schedulerTypes.POST_APPROVED_ACTION_REQUIRED:
                    case schedulerTypes.POST_REJECTED_INFO:
                    case schedulerTypes.POST_REJECTED_ACTION_REQUIRED:
                    case schedulerTypes.MULTIPOST_APPROVED_INFO:
                    case schedulerTypes.MULTIPOST_APPROVED_ACTION_REQUIRED:
                        return {
                            date: formatDateTime(
                                notification.data?.scheduler_post?.scheduled_at,
                                user?.settings?.date_format
                            ),
                            hour: formatDateTime(
                                notification.data?.scheduler_post?.scheduled_at,
                                `${userTimeFormat}_hour_minutes`
                            )
                        };

                    case schedulerTypes.POST_PUBLISHED_FAILED:
                        return {
                            social_network: selectedSocialProfile?.social_network_type
                        };

                    default:
                        return null;
                }
            }
        };
    };

    const config = getNotificationConfig();
    if (!config) return null;

    const renderNotificationContent = () => (
        <>
            <Notification.Title
                type="primary"
                i18nKey={config.title}
                values={config.getTitleValues()}
            />
            <Notification.Description
                type="primary"
                i18nKey={config.description}
                values={config.getDescriptionValues()}
            />
        </>
    );

    return (
        <Notification
            onClick={() => {
                if (selectedSocialProfile) {
                    setActiveSocialProfile(selectedSocialProfile);
                    router.push(url);
                }
            }}
            dataNotification={notification}
            contentHidden={notification?.options?.hideContent ?? false}
            wide={notification?.options?.wide ?? true}>
            <div className="relative">
                <PostThumbnail
                    thumbnail={notification.data?.scheduler_post?.media_thumbnail}
                    postType={notification.data?.scheduler_post?.type ?? 'image'}
                />
                <div
                    className={`absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full ${config.bgColor} text-8 text-white`}>
                    <Icon icon={config.icon} />
                </div>
                <div className="absolute -bottom-1.5 -right-1.5">
                    <Notification.Avatar size="xs" socialProfile={selectedSocialProfile} />
                </div>
            </div>
            <Notification.Content>{renderNotificationContent()}</Notification.Content>
        </Notification>
    );
};

// Export both individual components for backward compatibility
export const PostUpdated = SchedulerNotification;
export const PostPublishFailed = SchedulerNotification;

export default SchedulerNotification;
